import { getMeiliSearchClient } from '@/search/meilisearch'
import type { CollectionAfterChangeHook, FieldHook } from 'payload'

export const formatSlug = (val: string): string =>
  val
    .replace(/ /g, '-')
    .replace(/[^\w-]+/g, '')
    .toLowerCase()

export const formatSlugHook =
  (fallback: string): FieldHook =>
  ({ data, operation, value }) => {
    if (typeof value === 'string') {
      return formatSlug(value)
    }

    if (operation === 'create' || !data?.slug) {
      const fallbackData = data?.[fallback] || data?.[fallback]

      if (fallbackData && typeof fallbackData === 'string') {
        return formatSlug(fallbackData)
      }
    }

    return value
  }

export const formatSlugWithIdHook = (fallback: string): FieldHook => 
  ({ data, operation, value, originalDoc }) => {
     // For new documents, just format the slug without ID
    if (operation === 'create') {
      if (typeof value === 'string' && value.trim() !== '') {
        return formatSlug(value)
      } else if (data?.[fallback]) {
        const fallbackData = data[fallback]
        if (typeof fallbackData === 'string') {
          return formatSlug(fallbackData)
        }
      }
      return value
    }
    
    // For updates, check if ID is already present
    if (operation === 'update') {
      let baseSlug = value
      
      // If slug is empty, generate from fallback field
      if (!value && data?.[fallback]) {
        const fallbackData = data[fallback]
        if (typeof fallbackData === 'string') {
          baseSlug = formatSlug(fallbackData)
        }
      }
      
      // Remove any existing ID prefix to avoid duplication
      if (baseSlug && typeof baseSlug === 'string') {
        baseSlug = baseSlug.replace(/^\d+-/, '')
      }
      
      const id = data?.id || originalDoc?.id
      if (id && baseSlug) {
        return `${id}-${baseSlug}`
      }
    }
    
    return value
  }

export const syncSlugAndMeiliSearch: CollectionAfterChangeHook = async ({
  doc,
  operation,
  req,
}) => {
  try {
    let cleanSlug = doc.slug || ''

    // --- CREATE ---
    if (operation === 'create') {
      cleanSlug = cleanSlug.replace(/^\d+-/, '')
      const finalSlug = `${doc.id}-${formatSlug(cleanSlug)}`
      if (finalSlug !== doc.slug) {
        const updated = await req.payload.update({
          collection: 'properties',
          id: doc.id,
          data: { slug: finalSlug },
          depth: 0,
          req,
        })
        doc = updated
      }
    }

    // --- UPDATE ---
    if (operation === 'update') {
      cleanSlug = cleanSlug.replace(/^\d+-/, '')
      const finalSlug = `${doc.id}-${formatSlug(cleanSlug)}`
      if (finalSlug !== doc.slug) {
        const updated = await req.payload.update({
          collection: 'properties',
          id: doc.id,
          data: { slug: finalSlug },
          depth: 0,
          req,
        })
        doc = updated
      }
    }

    // --- Index in Meilisearch ---
    const client = getMeiliSearchClient()
    const index = client.index('properties-test3')
    const record = JSON.parse(JSON.stringify(doc))

    const res = await index.addDocuments([record])
    req.payload.logger.info(`Property indexed successfully`)
    req.payload.logger.info(res)
  } catch (error: any) {
    req.payload.logger.error(
      `Error syncing slug & Meilisearch: ${error.message}`,
    )
  }
  return doc
}